/**
 * SafeHer App - User Performance Optimizer
 * 
 * This utility handles special performance optimizations for specific users
 * to resolve rendering issues like flickering/flashing that might occur for
 * certain user IDs, particularly in development environments.
 */

// List of user IDs that need performance optimizations
const USERS_NEEDING_OPTIMIZATION = [
  
];

/**
 * Check if the current user needs performance optimizations
 * @param {Object} user - User object with ID 
 * @returns {boolean} - Whether user needs optimization
 */
export const needsOptimization = (user) => {
  if (!user || !user.id) return false;
  return USERS_NEEDING_OPTIMIZATION.includes(user.id);
};

/**
 * Apply performance optimizations for specific users
 * @param {Object} user - User object 
 * @returns {Object} - Performance settings to apply
 */
export const getUserOptimizations = (user) => {
  if (!needsOptimization(user)) {
    return {
      optimized: false,
      throttleUpdates: false,
      useReducedMotion: false,
      disableDynamicRefresh: false
    };
  }

  // Default optimizations for users with flashing issues
  return {
    optimized: true,
    throttleUpdates: true, // Slow down state updates
    useReducedMotion: true, // Disable animations
    disableDynamicRefresh: true, // Disable dynamic refreshing of data
    refreshInterval: 120 // Use a much longer refresh interval (seconds)
  };
};

/**
 * Apply debounce to a function for optimized users
 * @param {Function} fn - Function to debounce 
 * @param {number} delay - Delay in ms
 * @param {Object} user - User object
 * @returns {Function} - Debounced function if needed
 */
export const optimizedDebounce = (fn, delay = 500, user) => {
  let timer;
  
  return function(...args) {
    // For optimized users, use a longer debounce time
    const actualDelay = needsOptimization(user) ? delay * 3 : delay;
    
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, actualDelay);
  };
};
